import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  EventDiscoveryClient,
  EventDiscoveryQuotaConfig,
} from 'src/WebApiController';

export const useEventDiscoveryQuota = (
  viagogoEventId: number | null,
  disabled?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { trackError } = useClientLogContext();

  const shouldQuery =
    !disabled &&
    Boolean(activeAccountWebClientConfig.activeAccountId) &&
    Boolean(viagogoEventId);
  const tryAccessOnSaleEventQuery = useQuery({
    queryKey: [
      'EventDiscoveryClient.getOnSaleEventAccountAccess',
      viagogoEventId,
      disabled,
    ],
    queryFn: async () => {
      if (!shouldQuery || !viagogoEventId) {
        return null;
      }

      const eventDiscoveryQuota = await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).tryAccessOnSaleEvent(viagogoEventId);

      return eventDiscoveryQuota;
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 2, // 2 minutes
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('EventDiscoveryClient.getEventDiscoveryQuota', error);
      },
    },
  });

  const unlocakDemandDataMutation = useMutation({
    mutationFn: async ({
      quotaConfig,
    }: {
      quotaConfig: EventDiscoveryQuotaConfig;
    }) => {
      return await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).unlockEventDemandData(quotaConfig);
    },
    onError: (err: ErrorTypes, { quotaConfig }) => {
      trackError('EventDiscoveryClient.unlockEventDemandData', err, {
        trackErrorData: { quotaConfig },
      });
    },
    onSuccess: (value) => {
      if (value) {
        tryAccessOnSaleEventQuery.refetch();
      }
    },
  });

  const unlockDemandData = useCallback(
    async (quotaConfig: EventDiscoveryQuotaConfig) => {
      await unlocakDemandDataMutation.mutateAsync({ quotaConfig });
    },
    [unlocakDemandDataMutation]
  );

  return {
    onSaleEventAccess: tryAccessOnSaleEventQuery.data,
    isLoadingAccess: tryAccessOnSaleEventQuery.isLoading,
    isLoading: tryAccessOnSaleEventQuery.isLoading,
    unlockDemandData,
  };
};
